<template>
  <span
    class="app-image-lightbox"
    role="button"
    tabindex="0"
    @click="onOpen"
    @keydown.enter.prevent="onOpen"
    @keydown.space.prevent="onOpen"
  >
    <slot />
  </span>
</template>

<script lang="ts">
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import { CatchError } from "@/app/shared/services/helper/catch-helper";
import { BaseComponent } from "@/app/shared/components/base-component/base-component";
import AppImageLightboxOverlay from "./app-image-lightbox-overlay.vue";
import type { ImageLightboxBox } from "./types";

@Component({
  name: "app-image-lightbox",
})
export default class AppImageLightbox extends BaseComponent {
  @Prop({ required: true }) src!: string;
  @Prop({ default: "" }) downloadFilename!: string;
  /**
   * Rectangles to draw over the opened image, in percent of it. The thumbnail
   * in the slot draws its own; these are what the full-size view shows, so a
   * label can be checked at the size that makes it checkable.
   */
  @Prop({ default: () => [] }) boxes!: ImageLightboxBox[];

  private overlayInstance: any = null;
  private overlayEl: HTMLDivElement | null = null;

  mounted() {
    this.overlayEl = document.createElement("div");
    document.body.appendChild(this.overlayEl);
    const OverlayCtor = Vue.extend(AppImageLightboxOverlay);
    this.overlayInstance = new OverlayCtor().$mount(this.overlayEl);
    this.syncOverlayState();
  }

  beforeDestroy() {
    if (this.overlayInstance) {
      this.overlayInstance.$destroy();
    }
    if (this.overlayEl && this.overlayEl.parentNode) {
      this.overlayEl.parentNode.removeChild(this.overlayEl);
    }
  }

  @Watch("src")
  onSrcChanged() {
    this.syncOverlayState();
  }

  @Watch("downloadFilename")
  onDownloadFilenameChanged() {
    this.syncOverlayState();
  }

  @Watch("boxes")
  onBoxesChanged() {
    this.syncOverlayState();
  }

  private syncOverlayState() {
    if (!this.overlayInstance) {
      return;
    }

    this.overlayInstance.src = this.src;
    this.overlayInstance.downloadFilename = this.downloadFilename;
    this.overlayInstance.boxes = this.boxes;
  }

  @CatchError()
  onOpen() {
    if (this.overlayInstance) {
      this.syncOverlayState();
      this.overlayInstance.resetTransform();
      this.overlayInstance.open = true;
    }
  }
}
</script>

<style lang="scss">
.app-image-lightbox {
  cursor: pointer;
  display: inline-block;

  &-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.85);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    touch-action: none;
    overflow: hidden;
  }

  &-stage {
    backface-visibility: hidden;
    contain: layout style paint;
    display: flex;
    align-items: center;
    justify-content: center;
    isolation: isolate;
    transform: translate3d(0, 0, 0);
    will-change: transform;

    &--pannable {
      cursor: grab;
    }
  }

  &-overlay--interacting &-stage--pannable {
    cursor: grabbing;
  }

  &-actions {
    position: absolute;
    top: 1rem;
    right: 1rem;
    display: flex;
    gap: 0.5rem;
  }

  &-action {
    background: none;
    border: none;
    color: #fff;
    font-size: 1.5rem;
    cursor: pointer;
    line-height: 1;
    padding: 0.25rem;
    opacity: 0.8;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 1;
    }

    &:last-child {
      font-size: 2rem;
    }
  }

  &-image {
    display: block;
    max-width: 90vw;
    max-height: 90vh;
    object-fit: contain;
    pointer-events: none;
    user-select: none;
  }
}

.lightbox-enter-active,
.lightbox-leave-active {
  transition: opacity 0.25s ease;
}

.lightbox-enter,
.lightbox-leave-to {
  opacity: 0;
}

.lightbox-enter-to,
.lightbox-leave {
  opacity: 1;
}
</style>
